<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-app :style="customizerCssVars">
    <div
      class="template-shell"
      :class="{ 'content-edit-highlight-active': showContentEditHighlights }"
      :style="templateShellStyle"
    >
      <!-- GTM noscript -->
      <noscript v-if="gtm_code">
        <iframe :src="getGTMIFrameUrl()" height="0" width="0" style="display:none;visibility:hidden" />
      </noscript>

      <!-- Top App Bar -->
      <v-app-bar :min-height="headerHeightPx" :height="headerHeightPx" app :style="appBarStyle">
        <v-container>
          <v-row class="d-flex align-center flex-nowrap">
            <v-toolbar-title class="px-5">
              <BrandLogo
                v-if="headerLogo"
                :src="headerLogo"
                :link="formatURL(companyWebsite)"
                :height="headerLogoHeightPx"
                :max-width="headerLogoMaxWidthPx"
                :alt="store.company.company_name ? `${store.company.company_name} logo` : 'Company logo'"
                position="center left"
              />
            </v-toolbar-title>
            <v-btn
              variant="text"
              size="x-large"
              :href="`tel:${store.company.phone}`"
              target="_blank"
              class="font-weight-bold sw-phone-typography"
              :style="headerPhoneStyle"
            >
              {{ store.company.phone }}
            </v-btn>
          </v-row>
        </v-container>
      </v-app-bar>

      <!-- Main Content -->
      <v-main>
        <v-container fluid class="pa-0 mb-0">
          <slot />
        </v-container>
      </v-main>

      <v-dialog v-model="dialog" persistent max-width="575">
        <v-card>
          <v-card-title class="text-h5">You Need access</v-card-title>
          <v-card-text>This page is no longer active</v-card-text>
        </v-card>
      </v-dialog>

      <!-- Footer -->
      <AppFooter />
      <v-footer class="pa-0" color="surface-variant-darken-1">
        <v-col class="text-center text-body-2 on-surface-variant font-weight-medium ma-10">
          Copyright &copy; Stonewood Financial {{ new Date().getFullYear() }}. All Rights Reserved.
        </v-col>
      </v-footer>
    </div>

    <!-- Live Customizer — only when ?customize=true -->
    <CustomizeEditor
      v-if="isCustomizeMode"
      v-model:open="isCustomizerOpen"
      :drawer-width="CUSTOMIZER_DRAWER_WIDTH"
      :is-customize-mode="isCustomizeMode"
    />

    <div v-if="showContentAnchorButtons" class="content-anchor-layer">
      <button
        v-for="anchor in contentAnchorButtons"
        :key="anchor.key"
        class="content-anchor-btn"
        type="button"
        :title="`Edit ${anchor.slotId}`"
        :aria-label="`Edit ${anchor.slotId}`"
        :style="{ top: `${anchor.top}px`, left: `${anchor.left}px` }"
        @click.stop.prevent="focusContentSlot(anchor.slotId)"
      >
        <span class="mdi mdi-pencil" aria-hidden="true"></span>
      </button>
    </div>
  </v-app>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import BrandLogo from '@/components/BrandLogo.vue'
import AppFooter from '@/components/Footer.vue'
import CustomizeEditor from '@/components/CustomizeEditor.vue'
import { useBranding } from '@/composables/useBranding'
import { useLeadGeneratorStore } from '@/stores/leadGenerator'

const CUSTOMIZER_OPEN_STORAGE_KEY = 'rtb_customizer_open'
const CUSTOMIZER_STATE_STORAGE_KEY = 'rtb_customizer_ui_state'

const store = useLeadGeneratorStore()
const route = useRoute()

function readCustomizerOpenState() {
  if (typeof window === 'undefined') return null

  const rawValue = window.sessionStorage.getItem(CUSTOMIZER_OPEN_STORAGE_KEY)
  if (rawValue === 'true') return true
  if (rawValue === 'false') return false
  return null
}

function persistCustomizerOpenState(value) {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(CUSTOMIZER_OPEN_STORAGE_KEY, String(Boolean(value)))
}

function getRouteQueryValue(...keys) {
  const matchedValue = keys
    .flatMap((key) => [route.query[key], route.query[`amp;${key}`]])
    .find((value) => value !== undefined && value !== null && String(Array.isArray(value) ? value[0] : value).trim())
  const normalizedValue = Array.isArray(matchedValue) ? matchedValue[0] : matchedValue
  return String(normalizedValue || '').trim()
}

function isCustomizeQueryEnabled() {
  return getRouteQueryValue('customize').toLowerCase() === 'true'
}

function hasRequiredCustomizeQueryContext() {
  const sessionId = getRouteQueryValue('session_id', 'SESSION_ID', 'SessionID', 'sessionId', 'SESSION', 'session')
  const officeId = getRouteQueryValue('office_id', 'officeId', 'C', 'c')
  const authorId = getRouteQueryValue('U', 'u')

  return Boolean(sessionId && officeId && authorId)
}

const CUSTOMIZER_DRAWER_WIDTH = 480
const DEFAULT_HEADER_COLOR = '#FFFFFF'
const DEFAULT_HEADER_TEXT_COLOR = '#0F172A'
const DEFAULT_HEADER_HEIGHT_PX = 105
const MIN_HEADER_HEIGHT_PX = 72
const MAX_HEADER_HEIGHT_PX = 180
const BASE_LOGO_MAX_WIDTH_PX = 300
const BASE_HEADER_HEIGHT_PX = 105
const MAX_LOGO_MAX_WIDTH_PX = 560
const isCustomizeMode = computed(() => isCustomizeQueryEnabled() && hasRequiredCustomizeQueryContext())
const isCustomizerOpen = ref(readCustomizerOpenState() ?? isCustomizeMode.value)
// eslint-disable-next-line no-unused-vars
const { headerLogo, companyWebsite, gtm_code, dialog, getGTMIFrameUrl, formatURL } =
  useBranding()

const customizerOffset = computed(() => isCustomizeMode.value && isCustomizerOpen.value ? `${CUSTOMIZER_DRAWER_WIDTH}px` : '0px')
const customizerCssVars = computed(() => ({
  '--sw-customizer-width': `${CUSTOMIZER_DRAWER_WIDTH}px`,
}))
const templateShellStyle = computed(() => ({
  marginRight: customizerOffset.value,
}))

const headerColor = computed(() => {
  const rawColor = String(store.customization?.media?.header?.color || '').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(rawColor) ? rawColor : DEFAULT_HEADER_COLOR
})

const headerHeightPx = computed(() => {
  const parsed = parseInt(String(store.customization?.media?.header?.heightPx), 10)
  if (!Number.isFinite(parsed)) return DEFAULT_HEADER_HEIGHT_PX
  return Math.max(MIN_HEADER_HEIGHT_PX, Math.min(MAX_HEADER_HEIGHT_PX, parsed))
})

const headerTextColor = computed(() => {
  const rawColor = String(store.customization?.media?.header?.textColor || '').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(rawColor) ? rawColor : DEFAULT_HEADER_TEXT_COLOR
})

const headerLogoHeightPx = computed(() => Math.max(40, Math.round(headerHeightPx.value * 0.8)))
const headerLogoMaxWidthPx = computed(() => {
  const scaledMaxWidth = Math.round((headerHeightPx.value / BASE_HEADER_HEIGHT_PX) * BASE_LOGO_MAX_WIDTH_PX)
  return Math.max(BASE_LOGO_MAX_WIDTH_PX, Math.min(MAX_LOGO_MAX_WIDTH_PX, scaledMaxWidth))
})
const headerPhoneStyle = computed(() => ({
  color: headerTextColor.value,
}))

const appBarStyle = computed(() => ({
  right: customizerOffset.value,
  width: `calc(100% - ${customizerOffset.value})`,
  backgroundColor: headerColor.value,
}))

const contentAnchorButtons = ref([])
const isContentTabActive = ref(false)
const showContentEditHighlights = computed(
  () => isCustomizeMode.value && isCustomizerOpen.value && isContentTabActive.value
)
const showContentAnchorButtons = computed(
  () => isCustomizeMode.value && isCustomizerOpen.value && isContentTabActive.value && contentAnchorButtons.value.length > 0
)

let anchorRefreshRafId = 0
let contentAnchorObserver = null
let selectedContentTargetTimer = null
let liveContentTargetBindKey = ''

function readContentTabActiveState() {
  if (typeof window === 'undefined') return false

  try {
    const rawState = window.sessionStorage.getItem(CUSTOMIZER_STATE_STORAGE_KEY)
    const parsed = JSON.parse(rawState || '{}')
    return String(parsed?.activeTab || '').toLowerCase() === 'content'
  } catch {
    return false
  }
}

function syncContentTabActiveState() {
  isContentTabActive.value = readContentTabActiveState()
}

function scheduleContentAnchorRefresh() {
  if (typeof window === 'undefined') return
  if (anchorRefreshRafId) cancelAnimationFrame(anchorRefreshRafId)
  anchorRefreshRafId = requestAnimationFrame(() => {
    anchorRefreshRafId = 0
    refreshContentAnchors()
  })
}

function refreshContentAnchors() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  if (!isCustomizeMode.value || !isCustomizerOpen.value || !isContentTabActive.value) {
    contentAnchorButtons.value = []
    clearSelectedContentTargetHighlights()
    return
  }

  const targets = Array.from(document.querySelectorAll('[data-dynamic="content"][id]'))
  const viewportPadding = 8
  const anchorOffsetTop = 6
  const anchorOffsetLeft = 14
  const anchorSize = 36
  const scrollTop = window.scrollY || window.pageYOffset || 0
  const scrollLeft = window.scrollX || window.pageXOffset || 0
  const documentHeight = Math.max(
    document.documentElement?.scrollHeight || 0,
    document.body?.scrollHeight || 0,
    window.innerHeight || 0
  )
  const documentWidth = Math.max(
    document.documentElement?.scrollWidth || 0,
    document.body?.scrollWidth || 0,
    window.innerWidth || 0
  )
  const anchors = targets
    .map((el, index) => {
      const rect = el.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) return null

      const slotId = String(el.getAttribute('id') || '').trim()
      if (!slotId) return null

      return {
        key: `${slotId}-${index}`,
        slotId,
        top: Math.max(
          viewportPadding,
          Math.min(documentHeight - anchorSize, rect.top + scrollTop + anchorOffsetTop)
        ),
        left: Math.max(
          viewportPadding,
          Math.min(documentWidth - anchorSize, rect.right + scrollLeft - anchorOffsetLeft)
        ),
      }
    })
    .filter(Boolean)

  contentAnchorButtons.value = anchors
}

function clearSelectedContentTargetHighlights() {
  if (typeof document === 'undefined') return

  document
    .querySelectorAll('.content-edit-target-selected, .content-edit-target-container-selected')
    .forEach((node) => {
      node.classList.remove('content-edit-target-selected', 'content-edit-target-container-selected')
    })
}

function clearLiveContentTargetHighlights() {
  if (typeof document === 'undefined') return

  liveContentTargetBindKey = ''
  document
    .querySelectorAll('.content-edit-target-live, .content-edit-target-container-live')
    .forEach((node) => {
      node.classList.remove('content-edit-target-live', 'content-edit-target-container-live')
    })
}

function getContentSlotSelectorValue(slotId) {
  const raw = String(slotId || '').trim()
  if (!raw) return ''
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(raw)
  return raw.replace(/([#.;,[\]:"'\\])/g, '\\$1')
}

function getTemplateContentTargets(slotId, bindPath = '') {
  if (typeof document === 'undefined') return []

  const selectorValue = getContentSlotSelectorValue(slotId)
  if (!selectorValue) return []

  const normalizedBindPath = String(bindPath || '').trim()

  if (normalizedBindPath) {
    const escapedBindPath = getContentSlotSelectorValue(normalizedBindPath)
    const bindTargets = Array.from(document.querySelectorAll(`[data-content-slot="${selectorValue}"][data-content-bind="${escapedBindPath}"]`))
    if (bindTargets.length) return bindTargets
  }

  return Array.from(document.querySelectorAll(`[data-dynamic="content"][id="${selectorValue}"]`))
}

function highlightLiveContentTarget(slotId, bindPath = '') {
  const resolvedSlotId = String(slotId || '').trim()
  if (!resolvedSlotId) {
    clearLiveContentTargetHighlights()
    return
  }

  const targetKey = `${resolvedSlotId}::${String(bindPath || '').trim()}`
  if (targetKey === liveContentTargetBindKey) return

  liveContentTargetBindKey = targetKey
  clearLiveContentTargetHighlights()
  liveContentTargetBindKey = targetKey

  const targets = getTemplateContentTargets(resolvedSlotId, bindPath)
  targets.forEach((target) => {
    target.classList.add('content-edit-target-live')
  })
}

function highlightContentTarget(slotId) {
  if (typeof document === 'undefined') return

  clearSelectedContentTargetHighlights()

  const targets = getTemplateContentTargets(slotId)
  targets.forEach((target) => {
    target.classList.add('content-edit-target-selected')
  })

  if (selectedContentTargetTimer) {
    clearTimeout(selectedContentTargetTimer)
  }

  selectedContentTargetTimer = setTimeout(() => {
    clearSelectedContentTargetHighlights()
    selectedContentTargetTimer = null
  }, 2200)
}

function focusContentSlot(slotId) {
  const resolvedSlotId = String(slotId || '').trim()
  if (!resolvedSlotId || typeof window === 'undefined') return

  isCustomizerOpen.value = true
  highlightContentTarget(resolvedSlotId)
  window.dispatchEvent(new CustomEvent('rtb:focus-content-slot', {
    detail: { slotId: resolvedSlotId },
  }))
}

function onEditorContentHighlightEvent(event) {
  const isContentEditingActive = Boolean(isCustomizeMode.value && isCustomizerOpen.value && isContentTabActive.value)
  if (!isContentEditingActive) {
    clearLiveContentTargetHighlights()
    return
  }

  const isActive = Boolean(event?.detail?.active)
  if (!isActive) {
    clearLiveContentTargetHighlights()
    return
  }

  const slotId = String(event?.detail?.slotId || '').trim()
  const bindPath = String(event?.detail?.bindPath || '').trim()
  highlightLiveContentTarget(slotId, bindPath)
}

function onContentTabStateEvent(event) {
  isContentTabActive.value = Boolean(event?.detail?.active)
  scheduleContentAnchorRefresh()
}

watch(isCustomizeMode, (enabled) => {
  if (!enabled) {
    isCustomizerOpen.value = false
    clearSelectedContentTargetHighlights()
    clearLiveContentTargetHighlights()
    return
  }

  isCustomizerOpen.value = readCustomizerOpenState() ?? true
})

watch(isCustomizerOpen, (isOpen) => {
  persistCustomizerOpenState(isOpen)
  scheduleContentAnchorRefresh()
})

watch(
  [isCustomizeMode, () => route.fullPath],
  () => {
    syncContentTabActiveState()
    scheduleContentAnchorRefresh()
  },
  { immediate: true }
)

watch(showContentAnchorButtons, () => {
  scheduleContentAnchorRefresh()
})

onMounted(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  syncContentTabActiveState()
  nextTick(() => {
    scheduleContentAnchorRefresh()
  })

  window.addEventListener('scroll', scheduleContentAnchorRefresh, true)
  window.addEventListener('resize', scheduleContentAnchorRefresh)
  window.addEventListener('storage', syncContentTabActiveState)
  window.addEventListener('rtb:content-tab-state', onContentTabStateEvent)
  window.addEventListener('rtb:highlight-content-target', onEditorContentHighlightEvent)

  contentAnchorObserver = new MutationObserver(() => {
    scheduleContentAnchorRefresh()
  })
  contentAnchorObserver.observe(document.body, {
    attributes: true,
    childList: true,
    subtree: true,
  })
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', scheduleContentAnchorRefresh, true)
    window.removeEventListener('resize', scheduleContentAnchorRefresh)
    window.removeEventListener('storage', syncContentTabActiveState)
    window.removeEventListener('rtb:content-tab-state', onContentTabStateEvent)
    window.removeEventListener('rtb:highlight-content-target', onEditorContentHighlightEvent)
  }

  if (anchorRefreshRafId) {
    cancelAnimationFrame(anchorRefreshRafId)
    anchorRefreshRafId = 0
  }

  if (selectedContentTargetTimer) {
    clearTimeout(selectedContentTargetTimer)
    selectedContentTargetTimer = null
  }

  clearSelectedContentTargetHighlights()
  clearLiveContentTargetHighlights()

  if (contentAnchorObserver) {
    contentAnchorObserver.disconnect()
    contentAnchorObserver = null
  }
})

</script>

<style scoped>
.template-shell {
  min-height: 100%;
  transition: margin-right 0.2s ease;
}

.content-edit-highlight-active :deep([data-dynamic="content"]) {
  outline: 1px dotted rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.26) !important;
  outline-offset: 1px;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.06) !important;
  box-shadow: inset 0 0 0 1px rgba(var(--v-theme-customizer-primary, 30, 41, 59), 0.08);
  transition: outline-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.content-edit-highlight-active :deep([data-dynamic="content"]:hover) {
  outline-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.98) !important;
  background-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.34) !important;
  box-shadow:
    inset 0 0 0 1px rgba(var(--v-theme-customizer-primary, 30, 41, 59), 0.26),
    0 0 0 2px rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.28);
}

.content-edit-highlight-active :deep(.content-edit-target-selected) {
  outline-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 1) !important;
  background-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.34) !important;
  box-shadow:
    inset 0 0 0 1px rgba(var(--v-theme-customizer-primary, 30, 41, 59), 0.24),
    0 0 0 2px rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.34);
}

.content-edit-highlight-active :deep(.content-edit-target-live) {
  outline-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 1) !important;
  background-color: rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.42) !important;
  box-shadow:
    inset 0 0 0 1px rgba(var(--v-theme-customizer-primary, 30, 41, 59), 0.34),
    0 0 0 2px rgba(var(--v-theme-customizer-background, 248, 250, 252), 0.4);
}

.content-anchor-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.content-anchor-btn {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
  background: rgb(var(--v-theme-customizer-primary, 30 41 59));
  color: rgb(var(--v-theme-on-customizer-primary, 255 255 255));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
}

.content-anchor-btn:hover {
  transform: translateY(-1px);
}
.v-toolbar-title .v-toolbar-title__placeholder{
  overflow: visible !important;
}
</style>
